import { useCallback, useEffect, useState } from "react";
import { getIdToken } from "./lib/firebaseClient";
import { getPosters, getRuntimeConfig, setAuthTokenProvider, type RuntimeConfig } from "./lib/api";
import type { Poster } from "./lib/types";
import { AuthGate } from "./components/AuthGate";
import { Dashboard } from "./components/Dashboard";
import { Header } from "./components/Header";
import { Logger } from "./components/Logger";

export function App() {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [posters, setPosters] = useState<Poster[]>([]);
  const [route, setRoute] = useState(() => window.location.pathname === "/dashboard" ? "/dashboard" : "/logger");
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setPosters(await getPosters());
    } catch (refreshError) {
      setError(refreshError instanceof Error ? refreshError.message : "Unable to load data");
    }
  }, []);

  const handleAuthChange = useCallback(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setAuthTokenProvider(getIdToken);
    getRuntimeConfig()
      .then((runtimeConfig) => {
        setConfig(runtimeConfig);
        return refresh();
      })
      .catch((configError) => {
        setError(configError instanceof Error ? configError.message : "Unable to load config");
      });
  }, [refresh]);

  function changeRoute(nextRoute: string) {
    window.history.pushState({}, "", nextRoute);
    setRoute(nextRoute);
  }

  if (!config) return <div className="screen-center">Loading Posterly...</div>;

  return (
    <AuthGate config={config} onAuthChange={handleAuthChange}>
      <Header route={route} onRouteChange={changeRoute} />
      {error ? <div className="global-error">{error}</div> : null}
      {route === "/dashboard" ? (
        <Dashboard posters={posters} onRefresh={refresh} />
      ) : (
        <Logger posters={posters} onSaved={refresh} />
      )}
    </AuthGate>
  );
}
